/**
 * BlocStatut (zone [2]) — badges de statut/qualificatif + badge entité.
 *
 * Les badges arrivent DÉJÀ triés par priorité ; le front tronque à 3. Le badge
 * entité (Building2) n'apparaît que si l'entité du hit diffère de l'entité
 * courante (même règle que `SearchPreviewPanel`).
 */

import { Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { StatusChip } from '@/components/shared/StatusChip'
import { ClientStatusBadge } from '@/components/crm/ClientStatusBadge'
import type { ClientStatus } from '@/hooks/useClients.types'
import { SummaryIcon } from '../primitives'
import { toneChipClass } from '../primitives.helpers'
import type { SummaryBadge, SummaryEntity } from '../types'

const CHIP = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium'

export function SummaryBadges({
    badges,
    entity,
    currentEntityId,
}: {
    badges: SummaryBadge[]
    entity: SummaryEntity | null
    currentEntityId: string | null
}) {
    const { t } = useTranslation()
    const shown = badges.slice(0, 3)
    const otherEntity = entity && entity.id !== currentEntityId ? entity : null
    if (shown.length === 0 && !otherEntity) return null

    return (
        <div className="flex flex-wrap items-center gap-2">
            {shown.map((b, i) =>
                // Statut CLIENT → <ClientStatusBadge> EXACT de la fiche (shadcn Badge).
                b.client_status ? (
                    <ClientStatusBadge
                        key={`${b.label}-${i}`}
                        status={b.client_status as ClientStatus}
                    />
                ) : // Statut coloré (devis…) → pastille identique à la fiche (StatusChip
                // statique, pill color-mix theme-aware, sans icône).
                b.color ? (
                    <StatusChip
                        key={`${b.label}-${i}`}
                        current={{ id: '', label: t(b.label, b.label), color: b.color }}
                        size="sm"
                    />
                ) : (
                    <span key={`${b.label}-${i}`} className={cn(CHIP, toneChipClass(b.tone))}>
                        <SummaryIcon name={b.icon} className="h-3 w-3" />
                        {t(b.label, b.label)}
                    </span>
                ),
            )}
            {otherEntity && (
                <span
                    className={cn(CHIP, toneChipClass('primary'))}
                    title={t('summaryDrawer.entity', 'Entité')}
                >
                    <Building2 className="h-3 w-3" aria-hidden="true" />
                    {otherEntity.name}
                </span>
            )}
        </div>
    )
}
